import { useState } from 'react';
import { Outlet, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  CalendarDays,
  ClipboardList,
  User,
  Clock,
  FileText,
  Umbrella,
  PackageSearch,
  PackageMinus,
  Boxes,
  Menu,
  FileStack,
  ShoppingCart,
  Wallet,
} from 'lucide-react';
import { NavigationDrawer, Footer } from '@elka/ui';
import type { NavItemConfig } from '@elka/ui';
import { useIsMobile } from '@elka/hooks';
import { firstName } from '@elka/utils';
import { useAuth, type WorkerContext } from '@/features/auth/hooks/useAuth';
import BottomTabBar from './BottomTabBar';
import { useTypingInField } from './useTypingInField';
import { FirstVisitPushPrompt } from '@/components/FirstVisitPushPrompt';
import { OfflineBanner } from '@/components/OfflineBanner';
import { NotificationDropdown } from '@/features/notifications/components/NotificationDropdown';
import { AppModeProvider } from '@/features/settings/AppModeContext';
import { useAppMode, type AppMode } from '@/features/settings/appMode';
import { AppModeToggle } from '@/features/settings/AppModeToggle';
import packageJson from '../../../package.json';

const NAV_ITEMS: NavItemConfig[] = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'ראשי' },
  { to: '/schedule', icon: CalendarDays, label: 'הסידור שלי' },
  { to: '/reports/time', icon: Clock, label: 'שעות' },
  {
    to: '/reports/daily',
    icon: FileStack,
    label: 'טפסים',
    subItems: [
      { to: '/reports/daily', icon: ClipboardList, label: 'יומן עבודה' },
      { to: '/personal/purchase-requests', icon: ShoppingCart, label: 'בקשות רכש' },
    ],
  },
  {
    to: '/inventory/scan',
    icon: PackageSearch,
    label: 'מחסן',
    subItems: [
      { to: '/inventory/scan', icon: PackageSearch, label: 'איסוף' },
      { to: '/inventory/return', icon: PackageMinus, label: 'החזרה' },
      { to: '/inventory/items', icon: Boxes, label: 'פריטים' },
    ],
  },
  {
    to: '/personal/payslips',
    icon: User,
    label: 'אישי',
    subItems: [
      { to: '/personal/payslips', icon: FileText, label: 'תלושים' },
      { to: '/personal/vacation', icon: Umbrella, label: 'חופשה' },
      { to: '/personal/petty-cash', icon: Wallet, label: 'קופה קטנה' },
    ],
  },
];

function WorkerLayoutInner() {
  const { workerContext, signOut } = useAuth();
  const isMobile = useIsMobile();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const { mode } = useAppMode();
  // hidden while typing: iOS would otherwise lift it on top of the keyboard
  const typing = useTypingInField();
  const showBottomBar = isMobile && !typing;

  const isOffice = workerContext?.workerType === 'office';
  // full: everything. simple: no warehouse.
  const modeItems = NAV_ITEMS.filter((item) => mode === 'full' || item.to !== '/inventory/scan');
  // Office workers lose הסידור שלי entirely, and lose only יומן עבודה from within
  // the טפסים group (בקשות רכש stays) — filter subItems rather than dropping the
  // whole group, since '/reports/daily' is now the group's `to`, not a standalone item.
  const hideForOffice = (to: string) => to === '/reports/daily';
  const visibleNavItems = isOffice
    ? modeItems
        .filter((item) => item.to !== '/schedule')
        .map((item) =>
          item.subItems
            ? { ...item, subItems: item.subItems.filter((s) => !hideForOffice(s.to)) }
            : item
        )
    : modeItems;

  const displayName = firstName(workerContext?.workerName);
  const userInitial = displayName.charAt(0).toUpperCase();

  return (
    <div className="bg-page flex min-h-screen flex-col">
      <FirstVisitPushPrompt />

      {/* Calm top bar — flat surface, 1px line, hamburger + brand mark + actions.
          paddingTop keeps the bar clear of the iPhone status bar: index.html sets
          viewport-fit=cover, so in the standalone PWA the page starts BEHIND the
          notch. @elka/ui DesktopHeader carried this same inline style; the calm-v2
          shell (f9a1f45d) replaced that component and dropped it, which is what
          pushed the header under the status bar. Reads 0px everywhere else. */}
      <header
        className="bg-surface border-line sticky top-0 z-50 border-b"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 md:px-6">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="text-ink-2 hover:bg-surface-soft rounded-control -ms-2 flex h-11 w-11 items-center justify-center transition-[color,background-color,transform] active:scale-[0.96]"
            aria-label="פתח תפריט"
          >
            <Menu size={22} />
          </button>

          <Link to="/dashboard" className="flex items-center gap-2.5">
            <img
              src="/logo/elka-favicon.png"
              alt=""
              className="h-10 w-10 rounded-[10px] object-contain"
            />
            <span className="min-w-0 text-right">
              <span className="text-ink block text-base leading-tight font-extrabold">
                אלקה חשמל
              </span>
              <span className="text-muted block text-xs leading-tight font-semibold">
                אפליקציית צוות
              </span>
            </span>
          </Link>

          <div className="flex-1" />
          <NotificationDropdown />
        </div>
      </header>

      {/* Mobile / drawer navigation menu */}
      <NavigationDrawer
        calm
        brand={{
          logoSrc: '/logo/elka-favicon.png',
          title: 'אלקה חשמל',
          subtitle: 'אפליקציית צוות',
        }}
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        items={visibleNavItems}
        user={{ name: displayName, initial: userInitial }}
        userPanel={<AppModeToggle />}
        onSignOut={signOut}
      />

      {/* Main Content — extra bottom padding on mobile for the fixed BottomTabBar */}
      <main
        className={`mobile-safe-bottom flex-1 p-4 md:p-6 md:pb-6 lg:p-8 lg:pb-8 ${showBottomBar ? 'pb-20' : 'pb-4'}`}
      >
        <div className="mx-auto max-w-7xl">
          <OfflineBanner />
          <Outlet />
        </div>
      </main>

      {/* Footer — hidden on mobile where BottomTabBar is shown instead */}
      <Footer appTitle="פורטל עובדים אלקה" version={packageJson.version} hideOnMobile />

      {/* Mobile Bottom Tab Bar */}
      {showBottomBar && <BottomTabBar />}
    </div>
  );
}

/**
 * Admin-controlled modes. Office workers are pinned to simple
 * (or an admin-set appMode) with no in-app toggle; field workers choose for themselves.
 */
function forcedMode(ctx: WorkerContext | null): AppMode | undefined {
  if (!ctx) return undefined;
  if (ctx.workerType === 'office') return ctx.appMode ?? 'simple';
  return undefined;
}

export default function WorkerLayout() {
  const { workerContext } = useAuth();
  return (
    <AppModeProvider forced={forcedMode(workerContext)}>
      <WorkerLayoutInner />
    </AppModeProvider>
  );
}
